import { ApiError, desktop, type NotchMessage } from "@signet/web-kit";
import { suggestion, type Status } from "../shared/day.ts";
import { telegramApi, type TelegramStatus } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Jay's day, suggested from the MacBook notch (2026-10-08), in the desktop
   app. Every half minute it reads where the day is and asks shared/day.ts
   whether something is due: start at 8, lunch at 11:30, finish at 5:30, back
   once lunch is up. Send posts it; "In 15 min" and letting it close both
   bring it back in fifteen minutes. Back from lunch has no later: it stays
   until it is sent. Without a notch (an external screen, a locked Mac) it
   becomes a normal notification and tries the notch again later.

   The menu on the To-do page posts the same statuses; the next check sees
   the day has moved on and takes a suggestion down by itself.

   Starting work offers Already started instead of In 15 min (2026-11-05):
   it marks the day started without posting, for when he began long ago and
   a late message would only make Ben ask. The helper shows two buttons at
   most, and letting it close already brings it back in fifteen minutes.
   ────────────────────────────────────────────────────────────────────────── */

export const DAY_CHANGED = "signet:todo-day-changed";

const LATER_MS = 15 * 60_000;
const CHECK_MS = 30_000;

const PROMPT: Record<Status, { title: string; body: string; icon: "sun" | "lunch" | "back" | "moon"; label: string }> = {
  start: { title: "Time to start work", body: "Post Starting work to DAILY PLAN?", icon: "sun", label: "Starting work" },
  lunch: { title: "Lunch time", body: "Post Running on lunch to DAILY PLAN?", icon: "lunch", label: "Running on lunch" },
  back: { title: "Lunch is up", body: "Post Back from lunch to DAILY PLAN.", icon: "back", label: "Back from lunch" },
  finish: { title: "Time to finish up", body: "Post Finishing work with tomorrow's list?", icon: "moon", label: "Finishing work" },
};

const timeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

export function startReminders({ userId }: { userId: string }): () => void {
  const bridge = desktop;
  // An installed app from before the notch, or a browser: nothing to show it with.
  if (!bridge?.notch || !bridge.onNotch) return () => {};
  const notch = bridge.notch.bind(bridge);

  let stopped = false;
  let showing: Status | null = null;
  const laterKey = (status: Status) => `signet:todo:later:${userId}:${status}`;
  const laterUntil = (status: Status) => {
    try {
      return Number(localStorage.getItem(laterKey(status)) ?? 0);
    } catch {
      return 0;
    }
  };
  const later = (status: Status, ms = LATER_MS) => {
    try {
      localStorage.setItem(laterKey(status), String(Date.now() + ms));
    } catch {
      // Without storage it simply asks again at the next check.
    }
  };
  const hide = () => {
    if (!showing) return;
    void notch({ type: "hide", id: showing }).catch(() => {});
    showing = null;
  };

  const check = async () => {
    if (stopped) return;
    let status: TelegramStatus;
    try {
      status = await telegramApi.status();
    } catch (error) {
      // Not Jay's (404): stop for good. Anything else: try again at the next check.
      if (error instanceof ApiError && error.status === 404) stop();
      return;
    }
    if (stopped) return;
    const due = status.connected && status.topic ? suggestion(status.day, status.lunchAt ? new Date(status.lunchAt) : null, status.schedule, timeZone()) : null;
    if (!due) return hide();
    if (showing === due.status) return;
    if (Date.now() < laterUntil(due.status)) return hide();
    hide();
    const prompt = PROMPT[due.status];
    const message: NotchMessage = {
      type: "show",
      id: due.status,
      title: prompt.title,
      body: prompt.body,
      icon: prompt.icon,
      sticky: due.sticky,
      actions: due.sticky
        ? [{ id: "send", label: "Send", primary: true }]
        : [{ id: "send", label: "Send", primary: true }, due.status === "start" ? { id: "quiet", label: "Already started" } : { id: "later", label: "In 15 min" }],
    };
    showing = due.status;
    const ok = await notch(message).catch(() => false);
    if (!ok) {
      showing = null;
      later(due.status, 5 * 60_000);
    }
  };

  const send = async (status: Status, quiet = false) => {
    const label = PROMPT[status].label;
    try {
      const result = await telegramApi.say(status, quiet);
      if (!quiet && result.telegram !== "sent") void bridge.notify(`"${label}" didn't go`, "Telegram didn't take it. Try again from the Status menu.").catch(() => {});
    } catch (error) {
      void bridge.notify(`Couldn't post "${label}"`, error instanceof Error ? error.message : "Something went wrong.").catch(() => {});
    }
    window.dispatchEvent(new Event(DAY_CHANGED));
  };

  const off = bridge.onNotch((event) => {
    const status = event.id as Status;
    if (!(status in PROMPT)) return;
    if (event.type === "action") {
      if (status === showing) showing = null;
      if (event.action === "send") void send(status);
      else if (event.action === "quiet") void send(status, true);
      else later(status);
    } else if (event.type === "dismissed") {
      if (status === showing) showing = null;
      later(status);
    } else if (event.type === "unavailable") {
      if (status === showing) showing = null;
      const prompt = PROMPT[status];
      void bridge.notify(prompt.title, `${prompt.body} Open Signet's Status menu to post it.`).catch(() => {});
      later(status, status === "back" ? 5 * 60_000 : LATER_MS);
    }
  });

  const onChanged = () => void check();
  window.addEventListener(DAY_CHANGED, onChanged);
  void check();
  const timer = window.setInterval(() => void check(), CHECK_MS);

  function stop() {
    if (stopped) return;
    stopped = true;
    window.clearInterval(timer);
    window.removeEventListener(DAY_CHANGED, onChanged);
    off();
    hide();
  }
  return stop;
}
